/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import Pagination from "../../components/Pagination";
import {
  formatNumber,
  formatSubcontractorName,
  formatedAddress,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import MainBody from "../../components/MainBody/MainBody";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import AddEditAsset from "./AddEditAsset";
import { LucidePlusCircle } from "lucide-react";
import AssetActionButton from "./AssetActionButton";
import sortCaret from "../../components/SortCaret";
import CommonFilters from "../../components/CommonFilters";
import { useTrust } from "../../lib/context/TrustContext";
import { useOrganization } from "../../lib/context/OrganisationContext";
import UploadAsset from "./UploadAsset";
import Confirmation from "../../components/Confirmation";
import { cn } from "../../lib/utils";

// Here id => VenderId
const Assets = ({ id = "", expandList = false }) => {
  const navigate = useNavigate();

  const location = useLocation();
  const { organizationData } = useOrganization();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetList, setAssetList] = useState([]);
  const [page, setPage] = useState(expandList ? 1 : defaultPage || 1);
  const [limit, setLimit] = useState(
    expandList ? defaultLimitPagination : defaultLimit || defaultLimitPagination
  );
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();

  const [searchParam, setSearchParam] = useState(
    expandList ? "" : search || ""
  );
  const [searchParamData, setSearchParamData] = useState(
    expandList ? "" : search || ""
  );
  const [assetId, setAssetId] = useState(null);

  const [edit, setEdit] = useState(false);
  const [showAddEditAsset, setShowAddEditAsset] = React.useState(false);
  const [showAssetDelete, setShowAssetDelete] = useState(false);

  const [vendorId, setVendorId] = useState();

  const { trustId } = useTrust();

  const toggleShowAddEditAsset = () => {
    if (showAddEditAsset) {
      setEdit(false);
      setAssetId(null);
    }
    setShowAddEditAsset(!showAddEditAsset);
  };

  const toggleShowDelete = () => {
    if (showAssetDelete) {
      setAssetId(null);
    }
    setShowAssetDelete(!showAssetDelete);
  };

  var today = new Date();
  today.setHours(0, 0, 0, 0);

  // *API Call for Geting Assets - Start
  const fetchAssets = useCallback(
    async (passLimit, passPage) => {
      try {
        setAssetLoading(true);
        const { data } = await get(
          `${API.GET_ASSET}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&vendor_id=${vendorId ? vendorId : id ? id : ""}&trust_id=${
            trustId ? trustId : ""
          }`
        );
        setAssetLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const assetList = data.data.data;
        setAssetList(assetList);
        return data.data;
      } catch (error) {
        setAssetLoading(false);
        setAssetList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, searchParam, sortOrder, sortField, vendorId, trustId]
  );

  const handleDelete = () => {
    fetchAssets(limit, page);
  };

  // *API Call for Geting Assets - End

  // *Assets Table Row and Column Generation, Filtering and Sorting of Record - Start
  const assetGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        vendor_id: assetList[i]?.vendor_id,
        key: assetList[i]?.id,
        asset_id: assetList[i]?.id,
        code: assetList[i]?.code,
        asset_name: assetList[i]?.name,
        address: formatedAddress(
          assetList[i]?.street,
          assetList[i]?.suburb?.name,
          assetList[i]?.state?.short_name,
          assetList[i]?.postal_code
        ),
        estimated_value: assetList[i]?.estimated_value
          ? `$${formatNumber(Number(assetList[i]?.estimated_value))}`
          : "$0.00",
        vendor: assetList[i]?.vendor
          ? formatSubcontractorName(assetList[i]?.vendor)
          : "-",
        import_from: assetList[i]?.import_from
          ? assetList[i]?.import_from
          : "TrustSoft",
        action: (
          <AssetActionButton
            id={assetList[i]?.id}
            used={assetList[i]?.is_used ? true : false}
            toggleShowDelete={toggleShowDelete}
            handleDelete={handleDelete}
            setEdit={setEdit}
            setAssetId={setAssetId}
            toggleShowAddEditAsset={toggleShowAddEditAsset}
          />
        ),
        subscribe: <></>,
      });
    }
    return items;
  };

  const asset = assetGenerator(assetList?.length);

  const AssetColumns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "asset_name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "address",
      text: "Address",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "estimated_value",
      text: "Estimated/Reserved Value",
    },
    {
      headerClasses: cn(
        THClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      classes: cn(
        TDClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      dataField: "vendor",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "vendors",
        "Vendor"
      )}`,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchAssets(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchAssets(limit, page);
  };
  // *Assets Table Row and Column Generation, Filtering and Sorting of Record - End

  useEffect(() => {
    if (!expandList) {
      navigate(
        `?limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&search=${searchParam ? searchParam : ""}&sort_column=${
          sortField ? sortField : ""
        }&sort_order=${sortOrder ? sortOrder : ""}`,
        {
          replace: true,
        }
      );
    }
  }, [navigate, limit, searchParam, page, sortField, sortOrder, expandList]);

  // Single Asset Delete
  const handleAssetDelete = async () => {
    try {
      setAssetLoading(true);
      await deleteCall(`${API.ASSET_DELETE}/${assetId}`);
      setAssetLoading(false);
      fetchAssets(limit, page);
    } catch (error) {
      setAssetLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        expandList={expandList}
        mainTitle={getModuleNamingBySlug(
          organizationData?.organizationData,
          "assets",
          "Asset"
        )}
        actionContent={
          <>
            {/* Import Asset */}
            <UploadAsset
              fetchData={() => fetchAssets(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setAssetLoading}
              organizationData={organizationData}
            />

            {!expandList && (
              <CommonFilters
                vendor={true}
                setVendorId={setVendorId}
                vendorId={vendorId}
                trustId={trustId}
              />
            )}

            <ResponsiveDrawerForm
              description={""}
              title={
                edit
                  ? `Edit ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset"
                    )}`
                  : `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset"
                    )}`
              }
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setShowAddEditAsset(false);
              }}
              write={true}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>
                    Add{" "}
                    {getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "assets",
                      "Asset"
                    )}
                  </span>
                </div>
              }
              className={""}
              open={showAddEditAsset}
              buttonClassName={""}
              setOpen={setShowAddEditAsset}
            >
              <AddEditAsset
                edit={edit}
                trustId={trustId}
                toggleShowAddEditAsset={toggleShowAddEditAsset}
                assetID={assetId}
                fetchAssets={fetchAssets}
                expandList={expandList}
              />
            </ResponsiveDrawerForm>
          </>
        }
        mainClassName="w-[calc(100%-3rem)]"
        loading={assetLoading}
      >
        <div className="custom-table">
          <div className={"table-body-class"}>
            <BootstrapTable
              keyField="asset_id"
              remote
              data={asset}
              columns={AssetColumns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={`Delete ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "assets",
          "Asset"
        )}`}
        open={showAssetDelete}
        setOpen={setShowAssetDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={`Are you sure you want to delete ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "assets",
          "Asset"
        )?.toLowerCase()}?`}
        okAction={async () => {
          handleAssetDelete();
          toggleShowDelete();
          // fetchAssets(limit, page);
        }}
        cancelAction={() => {
          setAssetId(null);
          setShowAssetDelete(!showAssetDelete);
        }}
      />
    </>
  );
};
export default Assets;
